import { supabase } from '@/features/sistema/services/supabaseClient';

import { fileReaderService } from './fileReaderService';

type AlteracaoProjeto = {
  obra_id: string;
  data: string;        // date
  origem: 'relatorio'|'tarefa'|'comentario'|'servico'|'subetapa'|'requisicao'|string;
  origem_id: string;
  titulo: string|null;
  descricao: string|null;
  fonte_ref: string|null;
  score: number|null;
  tags: string[]|null;
  extras: any;
};

async function fetchAlteracoesPorObra(obraId: string, limit = 40): Promise<AlteracaoProjeto[]> {
  // 1) Tenta RPC (fn_alteracoes_por_obra)
  try {
    const rpc = await supabase.rpc('fn_alteracoes_por_obra', { p_obra: obraId, p_limit: limit });
    if (!rpc.error && Array.isArray(rpc.data)) return rpc.data as AlteracaoProjeto[];
  } catch {}
  // 2) Fallback na VIEW
  const { data, error } = await supabase
    .from('vw_alteracoes_projeto' as any) // exposta em schema acor; use "postgres" policy se necessário
    .select('*')
    .eq('obra_id', obraId)
    .order('data', { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  return data as unknown as AlteracaoProjeto[];
}

interface ObraCompleta {
  id: string;
  nome: string;
  cliente: string;
  endereco: string;
  valor_total: number;
  data_inicio: string;
  data_fim: string;
  status: string;
  responsavel: string;
  cronograma: any[];
  tarefas: any[];
  documentos: any[];
  gruposTarefas: any[];
  relatoriosRecentes: any[];
  fotosPorRelatorio: Record<string, any[]>;
  videosPorRelatorio: Record<string, any[]>;
  checklistInstances: any[];
  checklistItemsRecentes: any[];
  checklistTemplates: any[];
  documentosContent?: any[]; // Conteúdo dos arquivos com dados estruturados
}

class ContextService {
  
  /**
   * Busca contexto completo da obra para o ChatGPT (COM LEITURA DE ARQUIVOS)
   */
  async getObraCompleta(obraId: string): Promise<ObraCompleta | null> {
    try {
      // 1. Buscar dados básicos da obra com responsável
      const { data: obra, error: obraError } = await supabase
        .from('obras')
        .select(`
          *,
          supervisor:usuarios!obras_supervisor_id_fkey(nome, email),
          coordenador:usuarios!obras_coordenador_id_fkey(nome, email),
          estagiario:usuarios!obras_estagiario_id_fkey(nome, email)
        `)
        .eq('id', obraId)
        .single();

      if (obraError || !obra) {
        console.error('❌ Erro ao buscar obra:', obraError);
        return null;
      }

      // 2. Cronograma: agora fonte primária = subetapas_obra
      const { data: cronograma } = await supabase
        .from('subetapas_obra')
        .select(`
          *,
          responsavel:usuarios!subetapas_obra_responsavel_fk(nome, email)
        `)
        .eq('obra_id', obraId)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      // 3. Buscar grupos de tarefas
      const { data: gruposTarefas } = await supabase
        .from('grupos_tarefas')
        .select('*')
        .eq('obra_id', obraId)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      // 4. Buscar todas as tarefas com responsáveis
      const { data: tarefas } = await supabase
        .from('tarefas')
        .select(`
          *,
          responsavel:usuarios!tarefas_responsavel_id_fkey(nome, email),
          grupo:grupos_tarefas!tarefas_grupo_id_fkey(nome, descricao),
          responsaveis_multiplos:tarefas_responsaveis(
            usuario:usuarios!fk_tarefas_responsaveis_usuario(nome, email)
          )
        `)
        .eq('obra_id', obraId)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      
      // 5. Diários de obra (relatorios tipo relatorio_diario)
      const { data: relatoriosRecentes } = await supabase
        .from('relatorios')
        .select('id, data, status, atividades, ocorrencias, comentarios')
        .eq('obra_id', obraId)
        .eq('tipo', 'relatorio_diario')
        .order('data', { ascending: false })
        .limit(10);

      // 6. Fotos/Vídeos ligados aos diários (top 3 por diário)
      let fotosPorRelatorio: Record<string, any[]> = {};
      let videosPorRelatorio: Record<string, any[]> = {};
      if (relatoriosRecentes && relatoriosRecentes.length > 0) {
        const relIds = relatoriosRecentes.map((r: any) => r.id);
        const { data: fotos } = await supabase
          .from('fotos')
          .select('id, relatorio_id, url, criado_em, nome')
          .in('relatorio_id', relIds)
          .order('criado_em', { ascending: false });
        const { data: videos } = await supabase
          .from('videos')
          .select('id, relatorio_id, url, criado_em, nome')
          .in('relatorio_id', relIds)
          .order('criado_em', { ascending: false });
        if (fotos) for (const f of fotos) {
          const k = f.relatorio_id as string;
          if (!fotosPorRelatorio[k]) fotosPorRelatorio[k] = [];
          if (fotosPorRelatorio[k].length < 3) fotosPorRelatorio[k].push(f);
        }
        if (videos) for (const v of videos) {
          const k = v.relatorio_id as string;
          if (!videosPorRelatorio[k]) videosPorRelatorio[k] = [];
          if (videosPorRelatorio[k].length < 3) videosPorRelatorio[k].push(v);
        }
      }

      // 7. Checklist: instances → items → templates
      const { data: checklistInstances } = await supabase
        .from('checklist_instances')
        .select('id, template_id, created_at')
        .eq('obra_id', obraId)
        .order('created_at', { ascending: false });

      let checklistItemsRecentes: any[] = [];
      let checklistTemplates: any[] = [];
      if (checklistInstances && checklistInstances.length > 0) {
        const instIds = checklistInstances.map((i: any) => i.id);
        const { data: items } = await supabase
          .from('checklist_items')
          .select('id, checklist_instance_id, template_id, status, owner, comentario, updated_at')
          .in('checklist_instance_id', instIds)
          .order('updated_at', { ascending: false })
          .limit(100);
        checklistItemsRecentes = items || [];

        const templateIds = Array.from(new Set(checklistItemsRecentes.map((i: any) => i.template_id)));
        if (templateIds.length > 0) {
          const { data: templates } = await supabase
            .from('checklist_templates')
            .select('id, nome, disciplina, tipo, foto_modelo_url')
            .in('id', templateIds);
          checklistTemplates = templates || [];
        }
      }

      // 8. Buscar documentos da obra
      const { data: documentos, error: documentosError } = await supabase
        .from('documentos')
        .select('*')
        .eq('obra_id', obraId)
        .eq('eh_versao_atual', true)
        .order('disciplina', { ascending: true });

      if (documentosError) {
        console.error('⚠️ Erro ao buscar documentos:', documentosError);
      }

      // 6. 🆕 LER CONTEÚDO DOS ARQUIVOS IMPORTANTES (agora com dados estruturados)
      let documentosContent: any[] = [];
      
      if (documentos && documentos.length > 0) {
        console.log('📄 Iniciando leitura de arquivos importantes...');
        
        try {
          // Identificar documentos importantes (cronograma e orçamento)
          const documentosImportantes = documentos.filter((doc: any) => 
            doc.disciplina === 'cronograma' || 
            doc.disciplina === 'orcamento' ||
            doc.nome_arquivo.toLowerCase().includes('cronograma') ||
            doc.nome_arquivo.toLowerCase().includes('orcamento') ||
            doc.nome_arquivo.toLowerCase().includes('gantt')
          );

          console.log('📋 Documentos importantes encontrados:', documentosImportantes.length);

          // Ler conteúdo dos documentos importantes (máximo 3 para não atrasar)
          documentosContent = await fileReaderService.readObraDocuments(
            documentosImportantes.slice(0, 3)
          );

          console.log('✅ Documentos lidos com sucesso:', documentosContent.length);

        } catch (fileError) {
          console.warn('⚠️ Erro ao ler arquivos (continuando sem eles):', fileError);
          // Continuar sem os arquivos se houver erro
        }
      }

      const obraCompleta: ObraCompleta = {
        id: obra.id,
        nome: obra.nome,
        cliente: obra.cliente,
        endereco: obra.endereco || 'Não informado',
        valor_total: obra.valor_total || 0,
        data_inicio: obra.data_inicio,
        data_fim: obra.data_fim,
        status: obra.status,
        responsavel: obra.supervisor?.nome || obra.coordenador?.nome || obra.estagiario?.nome || 'Não definido',
        cronograma: cronograma || [],
        tarefas: tarefas || [],
        documentos: documentos || [],
        gruposTarefas: gruposTarefas || [],
        relatoriosRecentes: relatoriosRecentes || [],
        fotosPorRelatorio,
        videosPorRelatorio,
        checklistInstances: checklistInstances || [],
        checklistItemsRecentes,
        checklistTemplates,
        documentosContent: documentosContent // 🆕 Conteúdo dos arquivos
      };

      console.log('✅ Contexto completo da obra carregado:', {
        obra: obra.nome,
        tarefas: tarefas?.length || 0,
        cronograma: cronograma?.length || 0,
        documentos: documentos?.length || 0,
        arquivosLidos: documentosContent.length
      });

      return obraCompleta;

    } catch (error) {
      console.error('❌ Erro fatal ao buscar contexto da obra:', error);
      return null;
    }
  }

  /**
   * 🆕 GERA CONTEXTO ESTRUTURADO para o ChatGPT (PRIORIZA DADOS EXTRAÍDOS)
   */
  async generateContextText(obra: ObraCompleta): Promise<string> {
    const formatCurrency = (value: number) => 
      new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL' 
      }).format(value);

    const formatDate = (date: string) => {
      if (!date) return 'Não definido';
      return new Date(date).toLocaleDateString('pt-BR');
    };

    // 🎯 SEÇÃO 1: INFORMAÇÕES BÁSICAS DA OBRA
    let context = `=== OBRA ATUAL ===
Nome: ${obra.nome}
Cliente: ${obra.cliente}
Endereço: ${obra.endereco}
Valor Total: ${formatCurrency(obra.valor_total)}
Período: ${formatDate(obra.data_inicio)} até ${formatDate(obra.data_fim)}
Status: ${obra.status}
Responsável: ${obra.responsavel}

`;

    // 🎯 SEÇÃO 2: DADOS ESTRUTURADOS EXTRAÍDOS DOS ARQUIVOS (PRIORIDADE MÁXIMA)
    if (obra.documentosContent && obra.documentosContent.length > 0) {
      
      // Agregar todos os resumos de orçamento
      const resumosOrcamento: any[] = [];
      const resumosCronograma: any[] = [];
      
      obra.documentosContent.forEach(docContent => {
        if (docContent.structured?.resumoOrcamento) {
          resumosOrcamento.push({
            arquivo: docContent.fileName,
            ...docContent.structured.resumoOrcamento
          });
        }
        if (docContent.structured?.resumoCronograma) {
          resumosCronograma.push({
            arquivo: docContent.fileName,
            ...docContent.structured.resumoCronograma
          });
        }
      });

      // 💰 RESUMO CONSOLIDADO DE ORÇAMENTO
      if (resumosOrcamento.length > 0) {
        context += `=== 💰 ORÇAMENTO DETALHADO (EXTRAÍDO DOS ARQUIVOS) ===\n`;
        
        let totalGeralConsolidado = 0;
        const categoriasConsolidadas: { [cat: string]: any } = {};
        
        resumosOrcamento.forEach(resumo => {
          context += `\n📄 ARQUIVO: ${resumo.arquivo}\n`;
          context += `💵 VALOR TOTAL: ${formatCurrency(resumo.totalGeral)}\n`;
          
          totalGeralConsolidado += resumo.totalGeral;
          
          // 🎯 MOSTRAR DETALHES POR CATEGORIA (com itens específicos)
          Object.entries(resumo.categorias).forEach(([categoria, dados]: [string, any]) => {
            context += `\n🔹 ${categoria.toUpperCase().replace('_', ' ')}: ${formatCurrency(dados.valorTotal)} (${dados.itens} itens)\n`;
            
            // 🆕 MOSTRAR DETALHES DOS ITENS DA CATEGORIA
            if (dados.detalhes && dados.detalhes.length > 0) {
              dados.detalhes.forEach((item: any) => {
                context += `   • ${item.codigo} - ${item.descricao}: ${formatCurrency(item.valorTotal)}`;
                if (item.quantidade && item.unidade) {
                  context += ` (${item.quantidade} ${item.unidade})`;
                }
                context += `\n`;
              });
            }
            
            // Consolidar categorias
            if (!categoriasConsolidadas[categoria]) {
              categoriasConsolidadas[categoria] = {
                valorTotal: 0,
                valorMaterial: 0,
                valorMaoObra: 0,
                itens: 0,
                detalhes: []
              };
            }
            categoriasConsolidadas[categoria].valorTotal += dados.valorTotal;
            categoriasConsolidadas[categoria].valorMaterial += dados.valorMaterial;
            categoriasConsolidadas[categoria].valorMaoObra += dados.valorMaoObra;
            categoriasConsolidadas[categoria].itens += dados.itens;
            if (dados.detalhes) {
              categoriasConsolidadas[categoria].detalhes.push(...dados.detalhes);
            }
          });
        });
        
        // 📊 RESUMO CONSOLIDADO ESPECIAL PARA FORRO
        if (categoriasConsolidadas['forro']) {
          context += `\n🏗️ **RESUMO COMPLETO DE FORRO:**\n`;
          const forro = categoriasConsolidadas['forro'];
          context += `💰 **Valor Total de Forro**: ${formatCurrency(forro.valorTotal)}\n`;
          context += `📊 **Composição**:\n`;
          
          forro.detalhes.forEach((item: any) => {
            context += `   • ${item.descricao}: ${formatCurrency(item.valorTotal)}`;
            if (item.quantidade && item.unidade) {
              context += ` (${item.quantidade} ${item.unidade})`;
            }
            context += `\n`;
          });
          
          const percentual = ((forro.valorTotal / totalGeralConsolidado) * 100).toFixed(1);
          context += `📈 **Participação**: ${percentual}% do orçamento total\n`;
        }
        
        // 📊 RESUMO GERAL CONSOLIDADO
        if (resumosOrcamento.length > 1) {
          context += `\n📊 RESUMO CONSOLIDADO DE TODOS OS ARQUIVOS:\n`;
          context += `💰 **TOTAL GERAL**: ${formatCurrency(totalGeralConsolidado)}\n\n`;
          
          Object.entries(categoriasConsolidadas)
            .sort(([,a], [,b]) => b.valorTotal - a.valorTotal)
            .forEach(([categoria, dados]) => {
              const percentual = ((dados.valorTotal / totalGeralConsolidado) * 100).toFixed(1);
              context += `🏗️ **${categoria.toUpperCase().replace('_', ' ')}**: ${formatCurrency(dados.valorTotal)} (${percentual}%)\n`;
              if (dados.valorMaterial > 0) context += `   📦 Material: ${formatCurrency(dados.valorMaterial)}\n`;
              if (dados.valorMaoObra > 0) context += `   👷 Mão de obra: ${formatCurrency(dados.valorMaoObra)}\n`;
              context += `   📋 Itens: ${dados.itens}\n\n`;
            });
        }
        
        context += '\n';
      }

      // 📅 RESUMO CONSOLIDADO DE CRONOGRAMA
      if (resumosCronograma.length > 0) {
        context += `=== 📅 CRONOGRAMA DETALHADO (EXTRAÍDO DOS ARQUIVOS) ===\n`;
        
        resumosCronograma.forEach(resumo => {
          context += `\n📄 ARQUIVO: ${resumo.arquivo}\n`;
          context += `📋 TOTAL DE ATIVIDADES: ${resumo.totalAtividades}\n`;
          
          if (resumo.periodoExecucao?.inicio) {
            context += `📆 PERÍODO: ${resumo.periodoExecucao.inicio} até ${resumo.periodoExecucao.fim} (${resumo.periodoExecucao.duracaoTotal} dias)\n`;
          }
          
          // Mostrar por fases
          if (resumo.fases && Object.keys(resumo.fases).length > 0) {
            context += `\n🏗️ FASES DA OBRA:\n`;
            Object.entries(resumo.fases).forEach(([fase, dados]: [string, any]) => {
              context += `  🔸 ${fase.toUpperCase()}: ${dados.atividades} atividades (${dados.inicio} - ${dados.fim})\n`;
            });
          }
        });
        
        context += '\n';
      }

      // 📄 DETALHES DOS ARQUIVOS ANALISADOS
      context += `=== 📄 ARQUIVOS ANALISADOS (CONTEÚDO REAL) ===\n`;
      
      obra.documentosContent.forEach(docContent => {
        context += `\n📁 ARQUIVO: ${docContent.fileName}\n`;
        context += `📋 Tipo: ${docContent.type.toUpperCase()}\n`;
        
        // Mostrar itens específicos extraídos
        if (docContent.structured?.orcamento && docContent.structured.orcamento.length > 0) {
          context += `💰 ITENS DE ORÇAMENTO EXTRAÍDOS: ${docContent.structured.orcamento.length}\n`;
          
          // Mostrar primeiros 5 itens como exemplo
          docContent.structured.orcamento.slice(0, 5).forEach((item: any) => {
            if (item.valorTotal && item.valorTotal > 0) {
              context += `  • ${item.descricao} - ${formatCurrency(item.valorTotal)}\n`;
            }
          });
          
          if (docContent.structured.orcamento.length > 5) {
            context += `  ... e mais ${docContent.structured.orcamento.length - 5} itens\n`;
          }
        }
        
        if (docContent.structured?.cronograma && docContent.structured.cronograma.length > 0) {
          context += `📅 ATIVIDADES EXTRAÍDAS: ${docContent.structured.cronograma.length}\n`;
          
          // Mostrar primeiras 3 atividades como exemplo
          docContent.structured.cronograma.slice(0, 3).forEach((ativ: any) => {
            context += `  • ${ativ.atividade}`;
            if (ativ.dataInicio) context += ` (${ativ.dataInicio}`;
            if (ativ.dataFim && ativ.dataFim !== ativ.dataInicio) context += ` - ${ativ.dataFim}`;
            if (ativ.dataInicio) context += `)`;
            context += `\n`;
          });
          
          if (docContent.structured.cronograma.length > 3) {
            context += `  ... e mais ${docContent.structured.cronograma.length - 3} atividades\n`;
          }
        }
        
        context += '\n';
      });
    }

    // 🎯 SEÇÃO 3: DOCUMENTOS DISPONÍVEIS (lista completa)
    if (obra.documentos.length > 0) {
      context += `=== 📂 DOCUMENTOS DISPONÍVEIS NO SISTEMA ===\n`;
      
      const documentosPorDisciplina = obra.documentos.reduce((acc, doc) => {
        if (!acc[doc.disciplina]) acc[doc.disciplina] = [];
        acc[doc.disciplina].push(doc);
        return acc;
      }, {} as Record<string, any[]>);

      Object.entries(documentosPorDisciplina).forEach(([disciplina, docs]) => {
        context += `\n📁 ${disciplina.toUpperCase()}:\n`;
        (docs as any[]).forEach((doc: any) => {
          const analisado = obra.documentosContent?.some(dc => dc.fileName === doc.nome_arquivo);
          context += `  • ${doc.nome_arquivo} (${doc.versao})${analisado ? ' ✅ ANALISADO' : ''}\n`;
        });
      });
      context += '\n';
    }

    // 🎯 SEÇÃO 4: DIÁRIOS DE OBRA (últimos)
    if (obra.relatoriosRecentes && obra.relatoriosRecentes.length > 0) {
      context += `=== 📝 DIÁRIOS RECENTES ===\n`;
      const max = Math.min(obra.relatoriosRecentes.length, 7);
      for (let i = 0; i < max; i++) {
        const d = obra.relatoriosRecentes[i];
        const fotos = obra.fotosPorRelatorio?.[d.id]?.length || 0;
        const videos = obra.videosPorRelatorio?.[d.id]?.length || 0;
        const resumo = (d.atividades || d.ocorrencias || d.comentarios || '').toString().slice(0, 120).replace(/\s+/g,' ').trim();
        context += `• ${new Date(d.data).toLocaleDateString('pt-BR')} [${(d.status||'').toUpperCase()}] - fotos:${fotos} vídeos:${videos}${resumo ? ` — ${resumo}...` : ''}\n`;
      }
      context += '\n';
    }

    // 🎯 SEÇÃO 5: CHECKLIST (resumo 7–14 dias)
    if (obra.checklistItemsRecentes && obra.checklistItemsRecentes.length > 0) {
      context += `=== ✅ CHECKLIST (7–14 dias) ===\n`;
      const since = Date.now() - 14*24*60*60*1000;
      const items = obra.checklistItemsRecentes.filter((i:any)=> new Date(i.updated_at).getTime() >= since);
      const byTemplate: Record<string, number> = {};
      const byStatus: Record<string, number> = {};
      items.forEach((i:any)=>{
        byTemplate[i.template_id] = (byTemplate[i.template_id]||0) + 1;
        const st = (i.status||'').toString();
        byStatus[st] = (byStatus[st]||0) + 1;
      });
      const tplMap = new Map(obra.checklistTemplates.map((t:any)=>[t.id, t]));
      const disciplinaCount: Record<string, number> = {};
      Object.keys(byTemplate).forEach(tid=>{
        const t = tplMap.get(tid);
        const disc = t?.disciplina || 'Outros';
        disciplinaCount[disc] = (disciplinaCount[disc]||0)+byTemplate[tid];
      });
      context += `Statuses: ` + Object.entries(byStatus).map(([k,v])=>`${k}:${v}`).join(' | ') + `\n`;
      const topDisc = Object.entries(disciplinaCount).sort((a,b)=>b[1]-a[1]).slice(0,5);
      if (topDisc.length>0) {
        context += `Disciplinas com mais movimentação: ` + topDisc.map(([d,c])=>`${d}:${c}`).join(' | ') + `\n`;
      }
      const reprovados = items.filter((i:any)=> i.status === 'Reprovado').slice(0,10);
      if (reprovados.length>0) {
        context += `Itens reprovados recentes:\n`;
        reprovados.forEach((i:any, idx:number)=>{
          const t = tplMap.get(i.template_id);
          context += `  ${idx+1}. ${t?.disciplina || '—'} — ${t?.nome || t?.descricao_item || 'Item'} (${new Date(i.updated_at).toLocaleDateString('pt-BR')})\n`;
        });
      }
      context += '\n';
    }

    // 🎯 SEÇÃO 6: TAREFAS ESTRUTURADAS (sistema)
    if (obra.gruposTarefas.length > 0 && obra.tarefas.length > 0) {
      context += `=== ✅ TAREFAS DE EXECUÇÃO (SISTEMA) ===\n`;
      
      obra.gruposTarefas.forEach(grupo => {
        const tarefasDoGrupo = obra.tarefas.filter(t => t.grupo_id === grupo.id);
        if (tarefasDoGrupo.length > 0) {
          context += `\n📋 ${grupo.nome.toUpperCase()} (${tarefasDoGrupo.length} tarefas):\n`;
          
          tarefasDoGrupo.slice(0, 5).forEach(tarefa => { // Limitar a 5 por grupo
            const responsavel = tarefa.responsavel?.nome || 'Sem responsável';
            const status = tarefa.status.replace('_', ' ').toUpperCase();
            const prazo = tarefa.data_limite ? formatDate(tarefa.data_limite) : 'Sem prazo';
            
            context += `  • ${tarefa.nome} [${status}] - ${responsavel} (${prazo})\n`;
          });
          
          if (tarefasDoGrupo.length > 5) {
            context += `  ... e mais ${tarefasDoGrupo.length - 5} tarefas\n`;
          }
        }
      });
      context += '\n';
    }

    // 🎯 SEÇÃO 7: CRONOGRAMA ESTRUTURADO (se existir)
    if (obra.cronograma.length > 0) {
      context += `=== 📆 CRONOGRAMA ESTRUTURADO (SISTEMA) ===\n`;
      const itensAtivos = obra.cronograma.filter(item => 
        !item.completed && item.start_date
      ).slice(0, 8); // Limitar a 8 itens

      itensAtivos.forEach(item => {
        const inicio = formatDate(item.start_date);
        const fim = item.end_date ? formatDate(item.end_date) : 'Em aberto';
        const responsavel = item.owner?.nome || 'Não definido';
        
        context += `  • ${item.text} (${inicio} - ${fim}) - ${responsavel}\n`;
      });
      
      if (obra.cronograma.length > 8) {
        context += `  ... e mais ${obra.cronograma.length - 8} itens\n`;
      }
      context += '\n';
    }

    // 🎯 SEÇÃO 8: RESUMO EXECUTIVO FINAL
    const tarefasConcluidas = obra.tarefas.filter(t => t.status === 'concluido').length;
    const tarefasPendentes = obra.tarefas.filter(t => t.status !== 'concluido').length;
    const tarefasAtrasadas = obra.tarefas.filter(t => 
      t.data_limite && new Date(t.data_limite) < new Date() && t.status !== 'concluido'
    ).length;

    const arquivosAnalisados = obra.documentosContent?.length || 0;
    const temOrcamentoAnalisado = obra.documentosContent?.some(dc => 
      dc.structured?.resumoOrcamento && Object.keys(dc.structured.resumoOrcamento.categorias).length > 0
    );
    const temCronogramaAnalisado = obra.documentosContent?.some(dc => 
      dc.structured?.resumoCronograma && dc.structured.resumoCronograma.totalAtividades > 0
    );

    context += `=== 📊 RESUMO EXECUTIVO ===
📋 Total de tarefas no sistema: ${obra.tarefas.length}
• ✅ Concluídas: ${tarefasConcluidas}
• ⏳ Pendentes: ${tarefasPendentes}
• ⚠️ Atrasadas: ${tarefasAtrasadas}

📂 Documentos disponíveis: ${obra.documentos.length}
• 🤖 Arquivos analisados automaticamente: ${arquivosAnalisados}
• 💰 Orçamento extraído e estruturado: ${temOrcamentoAnalisado ? 'SIM' : 'NÃO'}
• 📅 Cronograma extraído e estruturado: ${temCronogramaAnalisado ? 'SIM' : 'NÃO'}

🎯 CAPACIDADES DE RESPOSTA:
${temOrcamentoAnalisado ? '✅ Posso responder perguntas específicas sobre custos, valores por categoria, itens do orçamento' : '⚠️ Orçamento não analisado - respostas limitadas'}
${temCronogramaAnalisado ? '✅ Posso responder sobre prazos, atividades, fases da obra' : '⚠️ Cronograma não analisado - consulte arquivos manualmente'}

💡 INSTRUÇÕES IMPORTANTES:
- Use os dados estruturados extraídos dos arquivos para responder perguntas específicas sobre valores e prazos
- Quando perguntado sobre custos de categorias (ex: "forro"), cite os valores exatos e itens que compõem essa categoria
- Para cronograma, use as datas e atividades específicas dos arquivos analisados
- Se a informação não estiver nos dados estruturados, mencione que pode estar em outros documentos da obra
- Sempre cite o arquivo de origem quando usar dados específicos

🎯 EXEMPLO DE RESPOSTA PARA "QUAL VALOR DO FORRO?":
"🏗️ **FORRO - VALORES DETALHADOS**
💰 **Valor Total**: R$ [valor exato do resumo]
📊 **Composição**: [listar itens com códigos e valores]
📁 **Fonte**: [nome do arquivo]
📈 **Participação**: [percentual] do orçamento total"
`;

    // === NOVO: Alterações de projeto (evidências) ===
    const alteracoes = await fetchAlteracoesPorObra(obra.id, 40);
    if (alteracoes.length > 0) {
      context += '\n[ALTERAÇÕES DE PROJETO]\n';
      for (const a of alteracoes) {
        const d = a.data ?? '';
        const origem = (a.origem || '').toLowerCase();
        const titulo = (a.titulo || '').trim();
        const fonte = a.fonte_ref || `${origem} ${a.origem_id}`;
        const linha =
          `- ${d} • ${origem} • ${titulo || '(sem título)'} — Fonte: ${fonte} — id:${a.origem_id}\n`;
        context += linha;
      }
    } else {
      context += '\n[ALTERAÇÕES DE PROJETO]\n- Sem registros na base unificada.\n';
    }

    return context;
  }
}

const contextService = new ContextService();
export { contextService };